import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import {
  ORGANISATION_TYPE_LABELS,
  DEFAULT_ORGANISATION_TYPE,
  WEEKDAYS,
  DEFAULT_WORKING_DAYS,
  INDIAN_STATES,
  DEFAULT_OPENING_TIME,
  DEFAULT_CLOSING_TIME,
} from '../../constants/organisation';
import { validateOrgOnboarding, hasErrors } from '../../utils/orgValidation';
import './OrgOnboardingModal.css';

/**
 * OrgOnboardingModal — Organisation onboarding form
 *
 * Displayed when session.onboarding_completed === false for org users.
 * Collects organisation details + operating hours in a single-page form.
 *
 * Props:
 *   isOpen       — boolean, controls visibility
 *   onSubmit     — async (formData) => void
 *   isLoading    — boolean, disables form during submission
 *   error        — string | null, server error message
 *   sessionData  — { email, phone, ... } from JWT/session for prefill
 */
const OrgOnboardingModal = ({
  isOpen,
  onSubmit,
  isLoading = false,
  error = null,
  sessionData = null,
}) => {
  const { theme } = useTheme();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    organisation_name: '',
    organisation_type: DEFAULT_ORGANISATION_TYPE,
    city: '',
    state: '',
    mobile_number: '',
    email: '',
    working_days: [...DEFAULT_WORKING_DAYS],
    opening_time: DEFAULT_OPENING_TIME,
    closing_time: DEFAULT_CLOSING_TIME,
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Pre-fill email & phone from session/JWT when available
  useEffect(() => {
    if (!sessionData) return;

    setFormData((prev) => ({
      ...prev,
      first_name: prev.first_name || sessionData.given_name || sessionData.firstName || '',
      last_name: prev.last_name || sessionData.family_name || sessionData.lastName || '',
      mobile_number: prev.mobile_number || sessionData.phone || sessionData.phone_number || '',
      email: prev.email || sessionData.email || '',
    }));
  }, [sessionData]);

  // ── Handlers ─────────────────────────────────────────────────────

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error on edit
    setValidationErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const toggleWorkingDay = useCallback((dayKey) => {
    setFormData((prev) => {
      const days = prev.working_days.includes(dayKey)
        ? prev.working_days.filter((d) => d !== dayKey)
        : [...prev.working_days, dayKey];
      return { ...prev, working_days: days };
    });

    // Clear working_days error on change
    setValidationErrors((prev) => {
      if (!prev.working_days) return prev;
      const next = { ...prev };
      delete next.working_days;
      return next;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateOrgOnboarding(formData);
    setValidationErrors(errors);

    if (hasErrors(errors)) return;

    await onSubmit(formData);
  };

  // ── Render ───────────────────────────────────────────────────────

  if (!isOpen) return null;

  const inputStyle = (fieldName) => ({
    backgroundColor: theme.auth?.inputBg || theme.background?.surface || '#fff',
    borderColor: validationErrors[fieldName]
      ? theme.brand?.error || '#DC2626'
      : theme.auth?.inputBorder || theme.border?.input || '#ccc',
    color: theme.auth?.inputText || theme.text?.primary || '#1F1F23',
  });

  const labelStyle = {
    color: theme.auth?.labelColor || theme.text?.secondary || '#6B6B70',
  };

  return (
    <div
      className="org-onboarding-overlay"
      style={{ backgroundColor: theme.background?.overlay || 'rgba(0,0,0,0.5)' }}
    >
      <div
        className="org-onboarding-modal"
        style={{
          backgroundColor: theme.auth?.containerBg || theme.background?.primary || '#fff',
          color: theme.auth?.containerText || theme.text?.primary || '#1F1F23',
          borderColor: theme.auth?.containerBorder || theme.border?.default || '#e5e5e5',
          boxShadow: theme.auth?.containerShadow || '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="org-onboarding-header">
          <h2>Set Up Your Organisation</h2>
          <p>Complete these details to get started — takes under 3 minutes</p>
        </div>

        {/* ── Server Error ───────────────────────────────────────── */}
        {error && (
          <div
            className="org-onboarding-error"
            style={{
              backgroundColor: theme.auth?.errorBg || '#FEE2E2',
              borderColor: theme.auth?.errorBorder || '#FCA5A5',
              color: theme.auth?.errorText || '#991B1B',
            }}
          >
            <span className="org-error-icon">⚠️</span>
            <div>
              <p className="org-error-message">{error}</p>
              <p className="org-error-hint">Please try again</p>
            </div>
          </div>
        )}

        {/* ── Form ───────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="org-onboarding-form" noValidate>

          {/* ── Section 1: Organisation Details ──────────────────── */}
          <fieldset className="org-section">
            <legend className="org-section-title" style={labelStyle}>
              Organisation Details
            </legend>

            {/* First Name & Last Name — side by side */}
            <div className="org-row">
              <div className="org-form-group org-half">
                <label htmlFor="first_name" style={labelStyle}>
                  First Name <span className="org-required">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="e.g. Rajesh"
                  style={inputStyle('first_name')}
                  disabled={isLoading}
                  autoFocus
                />
                {validationErrors.first_name && (
                  <span className="org-field-error">{validationErrors.first_name}</span>
                )}
              </div>

              <div className="org-form-group org-half">
                <label htmlFor="last_name" style={labelStyle}>
                  Last Name <span className="org-required">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="e.g. Sharma"
                  style={inputStyle('last_name')}
                  disabled={isLoading}
                />
                {validationErrors.last_name && (
                  <span className="org-field-error">{validationErrors.last_name}</span>
                )}
              </div>
            </div>

            {/* Organisation Name */}
            <div className="org-form-group">
              <label htmlFor="organisation_name" style={labelStyle}>
                Organisation Name <span className="org-required">*</span>
              </label>
              <input
                type="text"
                id="organisation_name"
                name="organisation_name"
                value={formData.organisation_name}
                onChange={handleChange}
                placeholder="e.g. Sunrise Clinic"
                style={inputStyle('organisation_name')}
                disabled={isLoading}
              />
              {validationErrors.organisation_name && (
                <span className="org-field-error">{validationErrors.organisation_name}</span>
              )}
            </div>

            {/* Organisation Type (disabled for MVP) */}
            <div className="org-form-group">
              <label htmlFor="organisation_type" style={labelStyle}>
                Organisation Type
              </label>
              <select
                id="organisation_type"
                name="organisation_type"
                value={formData.organisation_type}
                onChange={handleChange}
                style={inputStyle('organisation_type')}
                disabled={isLoading}
              >
                {Object.entries(ORGANISATION_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* City & State — side by side */}
            <div className="org-row">
              <div className="org-form-group org-half">
                <label htmlFor="city" style={labelStyle}>
                  City <span className="org-required">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g. Mumbai"
                  style={inputStyle('city')}
                  disabled={isLoading}
                />
                {validationErrors.city && (
                  <span className="org-field-error">{validationErrors.city}</span>
                )}
              </div>

              <div className="org-form-group org-half">
                <label htmlFor="state" style={labelStyle}>
                  State <span className="org-required">*</span>
                </label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  style={{
                    ...inputStyle('state'),
                    color: formData.state
                      ? (theme.auth?.inputText || theme.text?.primary)
                      : (theme.text?.placeholder || '#999'),
                  }}
                  disabled={isLoading}
                >
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {validationErrors.state && (
                  <span className="org-field-error">{validationErrors.state}</span>
                )}
              </div>
            </div>

            {/* Mobile & Email — side by side */}
            <div className="org-row">
              <div className="org-form-group org-half">
                <label htmlFor="mobile_number" style={labelStyle}>
                  Mobile <span className="org-required">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile_number"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  placeholder="10-digit mobile"
                  style={inputStyle('mobile_number')}
                  disabled={isLoading}
                />
                {validationErrors.mobile_number && (
                  <span className="org-field-error">{validationErrors.mobile_number}</span>
                )}
              </div>

              <div className="org-form-group org-half">
                <label htmlFor="email" style={labelStyle}>
                  Email <span className="org-required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@clinic.com"
                  style={inputStyle('email')}
                  disabled={isLoading}
                />
                {validationErrors.email && (
                  <span className="org-field-error">{validationErrors.email}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* ── Section 2: Operating Details ─────────────────────── */}
          <fieldset className="org-section">
            <legend className="org-section-title" style={labelStyle}>
              Operating Details
            </legend>

            {/* Working Days — pill toggles */}
            <div className="org-form-group">
              <label style={labelStyle}>
                Working Days <span className="org-required">*</span>
              </label>
              <div className="org-day-picker">
                {WEEKDAYS.map(({ key, label }) => {
                  const active = formData.working_days.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`org-day-pill ${active ? 'org-day-active' : ''}`}
                      onClick={() => toggleWorkingDay(key)}
                      disabled={isLoading}
                      style={{
                        backgroundColor: active
                          ? (theme.auth?.buttonPrimary || theme.brand?.purple || '#0f766e')
                          : (theme.auth?.inputBg || theme.background?.surface || '#f5f5f5'),
                        color: active
                          ? (theme.auth?.buttonText || '#fff')
                          : (theme.auth?.inputText || theme.text?.primary || '#333'),
                        borderColor: active
                          ? 'transparent'
                          : (theme.auth?.inputBorder || theme.border?.input || '#ccc'),
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {validationErrors.working_days && (
                <span className="org-field-error">{validationErrors.working_days}</span>
              )}
            </div>

            {/* Opening & Closing Time — side by side */}
            <div className="org-row">
              <div className="org-form-group org-half">
                <label htmlFor="opening_time" style={labelStyle}>
                  Opening Time <span className="org-required">*</span>
                </label>
                <input
                  type="time"
                  id="opening_time"
                  name="opening_time"
                  value={formData.opening_time}
                  onChange={handleChange}
                  style={inputStyle('opening_time')}
                  disabled={isLoading}
                />
                {validationErrors.opening_time && (
                  <span className="org-field-error">{validationErrors.opening_time}</span>
                )}
              </div>

              <div className="org-form-group org-half">
                <label htmlFor="closing_time" style={labelStyle}>
                  Closing Time <span className="org-required">*</span>
                </label>
                <input
                  type="time"
                  id="closing_time"
                  name="closing_time"
                  value={formData.closing_time}
                  onChange={handleChange}
                  style={inputStyle('closing_time')}
                  disabled={isLoading}
                />
                {validationErrors.closing_time && (
                  <span className="org-field-error">{validationErrors.closing_time}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* ── Submit ───────────────────────────────────────────── */}
          <button
            type="submit"
            className="org-onboarding-submit"
            style={{
              backgroundColor: theme.auth?.buttonPrimary || theme.brand?.purple || '#0f766e',
              color: theme.auth?.buttonText || '#fff',
              opacity: isLoading ? 0.7 : 1,
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Creating Organisation...' : 'Create Organisation'}
          </button>
        </form>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <p
          className="org-onboarding-footer"
          style={{ color: theme.auth?.mutedText || theme.text?.secondary || '#999' }}
        >
          Your data is secure and used only to set up your organisation workspace
        </p>
      </div>
    </div>
  );
};

export default OrgOnboardingModal;
